import { useState, useRef, useLayoutEffect, useEffect } from "react";
import { Drawer } from "../index";
import { Outlet, useLocation } from "react-router-dom";
import CssBaseline from "@mui/material/CssBaseline";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import AppBar from "../AppBar";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Loading from "../Loading";
import GlobalNotificationModal from "../NotificationDetailModal/GlobalNotificationModal";
import { isMeetingPreparePath } from "../../utils/meetingWindow";
import { LAYOUT_HEADER_HEIGHT } from "../../constants/appConstants";
import { appColors } from "../../constants/theme";

const Main = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [open, setOpen] = useState(true);
  const uiDisabled = useSelector(state => state.app.uiDisabled);
  const meetingJoinPending = useSelector(state => state.app.meetingJoinPending);
  const { t } = useTranslation();
  const { pathname } = useLocation();
  /** Görüşme rotasında navbar/drawer ilk karede görünmesin (Outlet mount olmadan önce) */
  const isVideoMeetingRoute = /\/appointments\/(meeting-prepare|meeting)\//.test(pathname);
  const hideLayoutChrome = uiDisabled || isVideoMeetingRoute;
  /** Hazırlık sekmesinde kendi Loading'i var; yarı saydam overlay + içerik = iki spinner */
  const suppressMeetingJoinOverlay = isMeetingPreparePath(pathname);
  const mainRef = useRef(null);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    if (mainRef.current) mainRef.current.scrollTop = 0;
  }, [pathname]);

  useEffect(() => {
    setOpen(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    if (isMobile) {
      setOpen(false);
    }
  }, [pathname, isMobile]);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: "100%",
        minHeight: "100vh",
        background: hideLayoutChrome ? appColors.bgMuted : (theme) => theme.palette.background.main,
      }}
    >
      <CssBaseline />
      {hideLayoutChrome ? null : (
        <AppBar open={open} isMobile={isMobile} handleDrawerOpen={handleDrawerOpen} />
      )}
      {hideLayoutChrome ? null : (
        <Drawer open={open} isMobile={isMobile} handleDrawerClose={handleDrawerClose} />
      )}
      <Box
        ref={mainRef}
        component="main"
        sx={{
          flexGrow: 1,
          // Flex child varsayılan minWidth:auto yüzünden tablo kadar genişler,
          // overflow hidden sağ tarafı keser ve iç scroll hiç oluşmaz.
          minWidth: 0,
          width: "100%",
          minHeight: hideLayoutChrome ? "100vh" : undefined,
          overflowX: "hidden",
          overflowY: "auto",
          backgroundColor: hideLayoutChrome ? appColors.bgMuted : "transparent",
          marginTop: hideLayoutChrome
            ? 0
            : { xs: "64px", md: `${LAYOUT_HEADER_HEIGHT}px` },
        }}
      >
        <Outlet />
      </Box>
      {meetingJoinPending && !suppressMeetingJoinOverlay ? (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: (theme) => theme.zIndex.modal + 10,
          }}
        >
          <Loading backdrop message={t("meeting_page_opening")} />
        </Box>
      ) : null}
      <GlobalNotificationModal />
    </Box>
  );
};

export default Main;
